import { useEffect, useRef, CSSProperties, useState } from "react";
import { useLocation } from "react-router-dom";
import { Badge } from "react-bootstrap";
import { useLanguage } from "../context/LanguageContext";

// Add TypeScript declaration for window.bitmedia
declare global {
  interface Window {
    bitmedia?: {
      reload: () => void;
    };
  }
}

// Define interface for component props
interface AdUnitProps {
  className?: string;
  width?: string;
  height?: string;
  containerStyle?: CSSProperties;
  centered?: boolean;
  showOnMobile?: boolean;
}

const AdUnit = ({
  className = "67cc9861aa72d3d47fe11a0b", // Default class name
  width = "320px",
  height = "100px",
  containerStyle = {},
  centered = true,
  showOnMobile = true,
}: AdUnitProps) => {
  const adRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!adRef.current) return;
    
    // Clear any existing content
    adRef.current.innerHTML = "";
    
    let isComponentMounted = true;
    
    const loadAd = () => {
      if (!isComponentMounted || !adRef.current) return;
      
      // Create ad container
      const adContainer = document.createElement("ins");
      adContainer.className = className;
      adContainer.style.display = "block";
      adContainer.style.width = width;
      adContainer.style.height = height;
      
      adRef.current.appendChild(adContainer);
      
      // Generate a unique timestamp for cache-busting
      const timestamp = Date.now();
      
      // Clean up old script if it exists
      const scriptId = `bitmedia-ad-script-${className}`;
      const existingScript = document.getElementById(scriptId);
      if (existingScript) {
        existingScript.remove();
      }
      
      // Add new script
      const script = document.createElement("script");
      script.id = scriptId;
      script.async = true;
      script.src = `https://cdn.bmcdn6.com/js/${className}.js?v=${timestamp}`;
      
      // Add error handling for the script
      script.onerror = () => {
        console.error("Failed to load ad script:", script.src);
      };
      
      // Add load handler for the script
      script.onload = () => {
        // if (!isComponentMounted) return;
        
        // First try using the reload API if available
        if (window.bitmedia && typeof window.bitmedia.reload === "function") {
          window.bitmedia.reload();
        }
        
        // Also try calling the global function that Bitmedia might register
        // This is a common pattern for ad networks
        const potentialFunctionName = `bm_${className}`;
        if (typeof window[potentialFunctionName as keyof Window] === "function") {
          try {
            (window[potentialFunctionName as keyof Window] as Function)();
          } catch (e) {
            console.error("Error calling Bitmedia function:", e);
          }
        }
      };
      
      adRef.current.appendChild(script);
    };
    
    // Load the ad with a slight delay to ensure DOM is ready
    const timeoutId = setTimeout(loadAd, 50);
    
    return () => {
      // isComponentMounted = false;
      clearTimeout(timeoutId);
      
      // Clean up
      if (adRef.current) {
        adRef.current.innerHTML = "";
      }
    };
  }, [className, width, height]); // Add adKey to dependencies to force re-run
  
  // Calculate max-width for outer container
  const numericWidth = parseInt(width, 10);
  const containerMaxWidth = numericWidth + 20; // Add extra space for padding
  
  // Create display class based on showOnMobile prop
  const displayClass = showOnMobile 
    ? "d-block d-md-none" 
    : "d-none d-md-flex position-relative ad-container";

  return (
    <div className={displayClass} style={{ marginTop: "20px", ...containerStyle }}>
      <div
        className="position-relative border rounded p-2"
        style={{
          width: "100%",
          maxWidth: `${containerMaxWidth}px`,
          margin: centered ? "0 auto" : undefined,
          overflow: "hidden",
          backgroundColor: "rgba(248, 249, 250, 0.5)",
        }}
      >
        <div
          className="position-absolute"
          style={{ top: "0", right: "1px", zIndex: 1 }}
        >
          <Badge bg="secondary">
            <small>Ad</small>
          </Badge>
        </div>

        <div
          ref={adRef}
          style={{
            width: "100%",
            textAlign: "center",
            position: "relative",
            minHeight: height,
          }}
        />
      </div>
    </div>
  );
};

export default AdUnit;